/**
 * Rapport des rachats appliqués — vue complète à exporter.
 *
 * 3 onglets :
 *   - Vue d'ensemble : KPIs + agrégations par pôle/niveau/option/classe + tranches
 *   - Liste détaillée : tableau étudiants rachetés + drilldown notes modifiées
 *   - Stats avancées : tranches moyennes avant, comparaison admis avec/sans rachat
 *
 * Exports :
 *   - Excel multi-feuilles (xlsx)
 *   - PDF officiel "PV de Rachat" avec en-tête institut + signature jury
 */
import React, { useMemo, useState } from 'react';
import {
  X, Loader, FileSpreadsheet, FileText, Filter, BarChart3, Users, Award,
  TrendingUp, ChevronRight, RefreshCw,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  useGetRapportRachatsQuery, RapportRachatsParams,
} from '../../services/rachatApi';
import {
  useGetPolesQuery, useGetOptionsQuery, useGetClassesQuery,
} from '../../services/polesOptionsApi';

interface Props {
  open: boolean;
  onClose: () => void;
}

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

const RapportRachatsModal: React.FC<Props> = ({ open, onClose }) => {
  const today = new Date().toISOString().slice(0, 10);
  // Par défaut : tous les rachats de l'année courante
  const debutAnneeScolaire = `${new Date().getFullYear() - (new Date().getMonth() < 6 ? 1 : 0)}-09-01`;

  const [tab, setTab] = useState<'apercu' | 'liste' | 'stats'>('apercu');
  const [dateMin, setDateMin] = useState(debutAnneeScolaire);
  const [dateMax, setDateMax] = useState(today);
  const [pole, setPole] = useState('');
  const [niveau, setNiveau] = useState('');
  const [option, setOption] = useState('');
  const [classe, setClasse] = useState('');
  const [cas, setCas] = useState<'' | 'sous_admission' | 'eliminatoire'>('');
  const [includeAnnules, setIncludeAnnules] = useState(false);

  const params: RapportRachatsParams = {
    date_min: dateMin || undefined,
    date_max: dateMax || undefined,
    pole: pole || undefined,
    niveau: niveau || undefined,
    option: option || undefined,
    classe: classe || undefined,
    cas: cas || undefined,
    include_annules: includeAnnules || undefined,
  };

  const { data, isFetching, refetch } = useGetRapportRachatsQuery(params, { skip: !open });
  const { data: poles } = useGetPolesQuery({ actif: true });
  const { data: options } = useGetOptionsQuery({ actif: true });
  const { data: classes } = useGetClassesQuery({ actif: true });

  const optionsFiltrees = useMemo(() => {
    const all = (options || []) as any[];
    if (!pole) return all;
    const p = (poles || []).find((x: any) => x.nom === pole);
    return p ? all.filter((o: any) => o.pole === p.id) : all;
  }, [options, poles, pole]);

  // ─── EXPORT EXCEL ────────────────────────────────────────────────
  const handleExportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // Feuille 1 : Vue d'ensemble
    const meta = [
      ['INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE 2I'],
      ['Rapport des Rachats Appliqués'],
      [`Période : ${data.meta.periode.date_min || '—'} → ${data.meta.periode.date_max || '—'}`],
      [`Filtres : pôle=${pole || 'tous'} · niveau=${niveau || 'tous'} · option=${option || 'toutes'} · classe=${classe || 'toutes'} · cas=${cas || 'tous'}`],
      [],
      ['INDICATEURS CLÉS'],
      ['Total étudiants rachetés', data.meta.total_etudiants],
      ['Admis grâce au rachat', data.meta.admis_grace_au_rachat],
      ['Admis avant rachat (UEs éliminatoires)', data.meta.admis_avant_rachat],
      ['Gain moyen', data.meta.gain_moyen],
      ['Gain max', data.meta.gain_max],
      ['Gain min', data.meta.gain_min],
      ['Cas sous-admission', data.meta.nb_sous_admission],
      ['Cas éliminatoire', data.meta.nb_eliminatoire],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(meta), 'Vue ensemble');

    // Feuille 2 : Liste étudiants
    const liste = [
      ['Matricule', 'Nom', 'Classe', 'Niveau', 'Option', 'Pôle', 'Cas',
       'Moy avant', 'Moy après', 'Gain', 'UEs validées avant', 'UEs validées après', 'UEs total',
       'Date rachat', 'Notes modifiées'],
      ...data.rachats.map(r => [
        r.matricule, r.nom, r.classe, r.niveau, r.option, r.pole, r.cas,
        r.moy_sem_avant ?? '', r.moy_sem_apres ?? '',
        (r.moy_sem_avant !== null && r.moy_sem_apres !== null)
          ? (r.moy_sem_apres - r.moy_sem_avant).toFixed(2) : '',
        r.nb_ues_validees_avant ?? '', r.nb_ues_validees_apres ?? '', r.nb_ues_total ?? '',
        r.date_application?.slice(0, 19).replace('T', ' ') || '',
        r.notes_modifiees.map(n =>
          `${n.matiere_code} (${n.source}: ${n.note_avant}→${n.note_apres})`
        ).join(' | '),
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(liste), 'Étudiants');

    // Feuille 3 : Stats par pôle/niveau/option/classe
    const stats = [
      ['STATISTIQUES PAR PÔLE'],
      ['Pôle', 'Étudiants rachetés', 'Admis grâce au rachat', 'Gain moyen'],
      ...data.par_pole.map(p => [p.libelle, p.nb_etu, p.nb_admis_grace_rachat, p.gain_moyen.toFixed(2)]),
      [],
      ['STATISTIQUES PAR NIVEAU'],
      ['Niveau', 'Étudiants rachetés', 'Admis grâce au rachat', 'Gain moyen'],
      ...data.par_niveau.map(p => [p.libelle, p.nb_etu, p.nb_admis_grace_rachat, p.gain_moyen.toFixed(2)]),
      [],
      ['STATISTIQUES PAR OPTION'],
      ['Option', 'Étudiants rachetés', 'Admis grâce au rachat', 'Gain moyen'],
      ...data.par_option.map(p => [p.libelle, p.nb_etu, p.nb_admis_grace_rachat, p.gain_moyen.toFixed(2)]),
      [],
      ['STATISTIQUES PAR CLASSE'],
      ['Classe', 'Étudiants rachetés', 'Admis grâce au rachat', 'Gain moyen'],
      ...data.par_classe.map(p => [p.libelle, p.nb_etu, p.nb_admis_grace_rachat, p.gain_moyen.toFixed(2)]),
      [],
      ['DISTRIBUTION PAR TRANCHE MOY AVANT'],
      ['Tranche', 'Étudiants', 'Admis grâce au rachat'],
      ...data.par_tranche_avant.map(p => [p.libelle, p.nb_etu, p.nb_admis_grace_rachat]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(stats), 'Statistiques');

    XLSX.writeFile(wb, `rapport_rachats_${today}.xlsx`);
  };

  // ─── EXPORT PDF OFFICIEL ─────────────────────────────────────────
  const handleExportPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 12;

    // En-tête institut
    doc.setFontSize(8); doc.setTextColor(30, 58, 138);
    doc.text("MINISTÈRE DE L'ENSEIGNEMENT SUPÉRIEUR", pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('DE LA RECHERCHE SCIENTIFIQUE ET DE L\'INNOVATION TECHNOLOGIQUE',
             pageWidth / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text('INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE 2I',
             pageWidth / 2, y, { align: 'center' });
    y += 5;
    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(100);
    doc.text('06, Rue LI-LELEMB Base Industrielle · POINTE-NOIRE · +242 06 736 10 00',
             pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('direction2i@institut-international-2i.com', pageWidth / 2, y, { align: 'center' });
    y += 8;

    // Ligne séparatrice
    doc.setDrawColor(30, 58, 138); doc.setLineWidth(0.6);
    doc.line(15, y, pageWidth - 15, y);
    y += 6;

    // Titre
    doc.setFontSize(16); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 138);
    doc.text('PROCÈS-VERBAL DE RACHAT', pageWidth / 2, y, { align: 'center' });
    y += 6;
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(80);
    doc.text(
      `Période : ${data.meta.periode.date_min || '—'} → ${data.meta.periode.date_max || '—'}`,
      pageWidth / 2, y, { align: 'center' }
    );
    y += 4;
    const scope = [
      pole && `Pôle : ${pole}`, niveau && `Niveau : ${niveau}`,
      option && `Option : ${option}`, classe && `Classe : ${classe}`,
      cas && `Cas : ${cas}`,
    ].filter(Boolean).join(' · ');
    if (scope) {
      doc.text(`Périmètre : ${scope}`, pageWidth / 2, y, { align: 'center' });
      y += 4;
    }
    y += 4;

    // KPIs
    doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(0);
    doc.text('Indicateurs clés', 15, y); y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Indicateur', 'Valeur']],
      body: [
        ['Total étudiants rachetés', String(data.meta.total_etudiants)],
        ['Admis GRÂCE au rachat', String(data.meta.admis_grace_au_rachat)],
        ['Cas sous-admission (moy 8-9.99)', String(data.meta.nb_sous_admission)],
        ['Cas UE éliminatoire (moy ≥ 10)', String(data.meta.nb_eliminatoire)],
        ['Gain moyen / étudiant', `+${data.meta.gain_moyen.toFixed(2)} pts`],
        ['Gain max', `+${data.meta.gain_max.toFixed(2)} pts`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138], textColor: 255, fontSize: 9 },
      styles: { fontSize: 8.5, cellPadding: 1.5 },
      margin: { left: 15, right: 15 },
    });
    y = (doc as any).lastAutoTable.finalY + 5;

    // Stats par pôle
    if (data.par_pole.length > 0) {
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.text('Répartition par pôle', 15, y); y += 1;
      autoTable(doc, {
        startY: y + 2,
        head: [['Pôle', 'Étudiants', 'Admis grâce rachat', 'Gain moy']],
        body: data.par_pole.map(p => [
          p.libelle, String(p.nb_etu), String(p.nb_admis_grace_rachat),
          `+${p.gain_moyen.toFixed(2)}`,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [30, 58, 138], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8.5, cellPadding: 1.5 },
        margin: { left: 15, right: 15 },
      });
      y = (doc as any).lastAutoTable.finalY + 5;
    }

    // Stats par niveau
    if (data.par_niveau.length > 0) {
      if (y > 240) { doc.addPage(); y = 15; }
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.text('Répartition par niveau', 15, y);
      autoTable(doc, {
        startY: y + 2,
        head: [['Niveau', 'Étudiants', 'Admis grâce rachat', 'Gain moy']],
        body: data.par_niveau.map(p => [
          p.libelle, String(p.nb_etu), String(p.nb_admis_grace_rachat),
          `+${p.gain_moyen.toFixed(2)}`,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [30, 58, 138], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8.5, cellPadding: 1.5 },
        margin: { left: 15, right: 15 },
      });
      y = (doc as any).lastAutoTable.finalY + 5;
    }

    // Stats par classe
    if (data.par_classe.length > 0) {
      if (y > 220) { doc.addPage(); y = 15; }
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.text('Répartition par classe', 15, y);
      autoTable(doc, {
        startY: y + 2,
        head: [['Classe', 'Étudiants', 'Admis grâce rachat', 'Gain moy']],
        body: data.par_classe.map(p => [
          p.libelle, String(p.nb_etu), String(p.nb_admis_grace_rachat),
          `+${p.gain_moyen.toFixed(2)}`,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [30, 58, 138], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8.5, cellPadding: 1.5 },
        margin: { left: 15, right: 15 },
      });
      y = (doc as any).lastAutoTable.finalY + 5;
    }

    // Liste détaillée — page suivante
    doc.addPage();
    y = 15;
    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 138);
    doc.text('LISTE DÉTAILLÉE DES ÉTUDIANTS RACHETÉS', pageWidth / 2, y, { align: 'center' });
    y += 5;
    autoTable(doc, {
      startY: y,
      head: [['Matricule', 'Nom', 'Classe', 'Moy avant', 'Moy après', 'Gain', 'Cas']],
      body: data.rachats.map(r => [
        r.matricule, r.nom.substring(0, 28), r.classe,
        r.moy_sem_avant?.toFixed(2) ?? '—',
        r.moy_sem_apres?.toFixed(2) ?? '—',
        (r.moy_sem_avant !== null && r.moy_sem_apres !== null)
          ? `+${(r.moy_sem_apres - r.moy_sem_avant).toFixed(2)}` : '—',
        r.cas === 'eliminatoire' ? 'Élim.' : 'Sous adm.',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 138], textColor: 255, fontSize: 8.5 },
      styles: { fontSize: 7.5, cellPadding: 1 },
      margin: { left: 10, right: 10 },
      didDrawPage: () => {
        // Header sur chaque page
        doc.setFontSize(7); doc.setTextColor(120);
        doc.text('INSTITUT INTERNATIONAL 2I — Procès-verbal de rachat',
                 pageWidth / 2, 8, { align: 'center' });
      },
    });

    // Signature jury (dernière page)
    let yFin = (doc as any).lastAutoTable.finalY + 15;
    if (yFin > 250) { doc.addPage(); yFin = 30; }
    doc.setFontSize(9); doc.setTextColor(0); doc.setFont('helvetica', 'normal');
    doc.text(`Fait à Pointe-Noire, le ${new Date().toLocaleDateString('fr-FR')}`, 15, yFin);
    yFin += 18;

    // Bloc signatures
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    const colW = (pageWidth - 30) / 3;
    ['Le Président du jury', 'Le Directeur des études', 'Le Secrétaire'].forEach((titre, i) => {
      const x = 15 + i * colW;
      doc.text(titre, x + colW / 2, yFin, { align: 'center' });
      doc.setLineWidth(0.2); doc.setDrawColor(80);
      doc.line(x + 10, yFin + 18, x + colW - 10, yFin + 18);
    });

    doc.save(`PV_rachat_${today}.pdf`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <FileText size={16} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">
              Rapport des Rachats Appliqués
            </h2>
            <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded p-0.5">
              {(['apercu', 'liste', 'stats'] as const).map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded ${
                    tab === t ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                              : 'text-gray-600 dark:text-gray-400'
                  }`}>
                  {t === 'apercu' ? 'Vue d\'ensemble' : t === 'liste' ? 'Liste' : 'Stats avancées'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => refetch()}
              className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded flex items-center gap-1">
              <RefreshCw size={11} /> Rafraîchir
            </button>
            <button onClick={handleExportExcel} disabled={!data}
              className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded font-medium flex items-center gap-1.5">
              <FileSpreadsheet size={12} /> Excel
            </button>
            <button onClick={handleExportPDF} disabled={!data}
              className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded font-medium flex items-center gap-1.5">
              <FileText size={12} /> PDF officiel
            </button>
            <button onClick={onClose}
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
            <Filter size={13} /> Filtres
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            <FilterField label="Du">
              <input type="date" value={dateMin} onChange={(e) => setDateMin(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
            </FilterField>
            <FilterField label="Au">
              <input type="date" value={dateMax} onChange={(e) => setDateMax(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
            </FilterField>
            <FilterField label="Pôle">
              <select value={pole} onChange={(e) => { setPole(e.target.value); setOption(''); }}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                <option value="">Tous</option>
                {(poles || []).map((p: any) => <option key={p.id} value={p.nom}>{p.nom}</option>)}
              </select>
            </FilterField>
            <FilterField label="Niveau">
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                <option value="">Tous</option>
                {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </FilterField>
            <FilterField label="Option">
              <select value={option} onChange={(e) => setOption(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                <option value="">Toutes</option>
                {optionsFiltrees.map((o: any) => <option key={o.id} value={o.nom}>{o.nom}</option>)}
              </select>
            </FilterField>
            <FilterField label="Classe">
              <select value={classe} onChange={(e) => setClasse(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                <option value="">Toutes</option>
                {(classes || []).map((c: any) => <option key={c.id || c.code} value={c.code}>{c.code}</option>)}
              </select>
            </FilterField>
            <FilterField label="Cas">
              <select value={cas} onChange={(e) => setCas(e.target.value as any)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                <option value="">Tous</option>
                <option value="sous_admission">Sous admission</option>
                <option value="eliminatoire">Éliminatoire</option>
              </select>
            </FilterField>
            <FilterField label="Annulés">
              <label className="flex items-center gap-1 text-[11px] mt-1">
                <input type="checkbox" checked={includeAnnules}
                  onChange={(e) => setIncludeAnnules(e.target.checked)} />
                Inclure
              </label>
            </FilterField>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {isFetching && !data && (
            <div className="text-center py-16">
              <Loader className="animate-spin mx-auto mb-2 text-blue-500" />
              <p className="text-sm text-gray-500">Chargement du rapport…</p>
            </div>
          )}
          {data && tab === 'apercu' && <ApercuView data={data} />}
          {data && tab === 'liste' && <ListeView data={data} />}
          {data && tab === 'stats' && <StatsView data={data} />}
        </div>
      </div>
    </div>
  );
};

const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">{label}</label>
    {children}
  </div>
);

// ─── Vue d'ensemble ──────────────────────────────────────────────────
const ApercuView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    {/* KPIs */}
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
      <Kpi label="Étudiants rachetés" value={data.meta.total_etudiants}
        icon={<Users size={16} />} couleur="text-blue-600" />
      <Kpi label="Admis grâce rachat" value={data.meta.admis_grace_au_rachat}
        icon={<Award size={16} />} couleur="text-green-600"
        hint={`sur ${data.meta.total_etudiants}`} />
      <Kpi label="Gain moyen" value={`+${data.meta.gain_moyen.toFixed(2)}`}
        icon={<TrendingUp size={16} />} couleur="text-purple-600" />
      <Kpi label="Gain max" value={`+${data.meta.gain_max.toFixed(2)}`}
        couleur="text-amber-600" />
      <Kpi label="Sous admission" value={data.meta.nb_sous_admission}
        couleur="text-blue-600" />
      <Kpi label="Éliminatoire" value={data.meta.nb_eliminatoire}
        couleur="text-orange-600" />
    </div>

    {/* Comparaison avant/après */}
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/15 dark:to-emerald-900/15 border border-green-200 dark:border-green-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
        <Award size={16} className="text-green-600" />
        Impact du rachat — admis avec vs sans rachat
      </h3>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
            {data.meta.admis_avant_rachat}
          </div>
          <div className="text-[11px] text-gray-500 uppercase">Admis avant le rachat</div>
          <div className="text-[10px] text-gray-400 italic">(UE éliminatoire bloquante)</div>
        </div>
        <div className="flex items-center justify-center text-3xl text-green-500 font-light">→</div>
        <div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {data.meta.admis_avant_rachat + data.meta.admis_grace_au_rachat}
          </div>
          <div className="text-[11px] text-gray-500 uppercase">Admis après le rachat</div>
          <div className="text-[10px] text-green-600 dark:text-green-400 font-semibold">
            +{data.meta.admis_grace_au_rachat} étudiants sauvés
          </div>
        </div>
      </div>
    </div>

    {/* Tableaux agrégation */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <AggregTable titre="Par pôle" rows={data.par_pole} />
      <AggregTable titre="Par niveau" rows={data.par_niveau} />
      <AggregTable titre="Par option" rows={data.par_option} />
      <AggregTable titre="Par classe" rows={data.par_classe} />
    </div>
  </div>
);

const AggregTable: React.FC<{ titre: string; rows: any[] }> = ({ titre, rows }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-xs font-bold text-gray-700 dark:text-gray-200">{titre}</h3>
    </div>
    {rows.length === 0 ? (
      <div className="p-4 text-center text-xs text-gray-500">Aucune donnée</div>
    ) : (
      <div className="overflow-x-auto max-h-72">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-700/20 text-[10px] uppercase text-gray-500 sticky top-0">
            <tr>
              <th className="px-2 py-1.5 text-left">Libellé</th>
              <th className="px-2 py-1.5 text-right">Étudiants</th>
              <th className="px-2 py-1.5 text-right">Sauvés</th>
              <th className="px-2 py-1.5 text-right">Gain moy</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <td className="px-2 py-1 font-medium truncate max-w-[180px]">{r.libelle}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.nb_etu}</td>
                <td className="px-2 py-1 text-right tabular-nums text-green-600 font-semibold">{r.nb_admis_grace_rachat}</td>
                <td className="px-2 py-1 text-right tabular-nums font-bold text-purple-600">+{r.gain_moyen.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ─── Liste détaillée ─────────────────────────────────────────────────
const ListeView: React.FC<{ data: any }> = ({ data }) => {
  const [open, setOpen] = useState<Record<number, boolean>>({});
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-700/50 text-[10px] uppercase text-gray-500 sticky top-0 z-10">
          <tr>
            <th className="px-2 py-2 w-6"></th>
            <th className="px-2 py-2 text-left">Matricule</th>
            <th className="px-2 py-2 text-left">Nom</th>
            <th className="px-2 py-2 text-left">Classe</th>
            <th className="px-2 py-2 text-left">Option</th>
            <th className="px-2 py-2 text-center">Cas</th>
            <th className="px-2 py-2 text-right">Moy avant</th>
            <th className="px-2 py-2 text-right">Moy après</th>
            <th className="px-2 py-2 text-right">Gain</th>
            <th className="px-2 py-2 text-center">UEs</th>
            <th className="px-2 py-2 text-center">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {data.rachats.map((r: any, idx: number) => {
            const gain = (r.moy_sem_avant !== null && r.moy_sem_apres !== null)
              ? r.moy_sem_apres - r.moy_sem_avant : null;
            const sauve = r.valide_apres === true && r.valide_avant === false;
            return (
              <React.Fragment key={`${r.batch_id}-${r.etudiant_id}-${idx}`}>
                <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                  r.is_annule ? 'opacity-50 line-through' : ''
                } ${sauve ? 'bg-green-50/40 dark:bg-green-900/10' : ''}`}>
                  <td className="px-1 py-1 text-center">
                    <button onClick={() => setOpen({ ...open, [idx]: !open[idx] })}
                      className="text-blue-600 hover:underline">
                      <ChevronRight size={12} className={open[idx] ? 'rotate-90 transition-transform' : ''} />
                    </button>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{r.matricule}</td>
                  <td className="px-2 py-1.5 font-medium">{r.nom}</td>
                  <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{r.classe}</td>
                  <td className="px-2 py-1.5 text-gray-500 text-[10px] truncate max-w-[140px]" title={r.option}>{r.option}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                      r.cas === 'eliminatoire'
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    }`}>
                      {r.cas === 'eliminatoire' ? 'Élim.' : 'Sous adm.'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.moy_sem_avant?.toFixed(2) ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold text-green-600 dark:text-green-400">
                    {r.moy_sem_apres?.toFixed(2) ?? '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                    {gain !== null && (
                      <span className={gain > 0 ? 'text-green-600' : 'text-gray-400'}>
                        {gain > 0 ? '+' : ''}{gain.toFixed(2)}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center text-[10px] tabular-nums">
                    {r.nb_ues_validees_avant ?? '—'} → <b className="text-green-600">{r.nb_ues_validees_apres ?? '—'}</b>/{r.nb_ues_total ?? '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center text-[10px] text-gray-500">
                    {r.date_application ? new Date(r.date_application).toLocaleDateString('fr-FR') : '—'}
                  </td>
                </tr>
                {open[idx] && (
                  <tr className="bg-gray-50 dark:bg-gray-700/30">
                    <td colSpan={11} className="px-4 py-2">
                      <div className="text-[10px] font-bold uppercase text-gray-500 mb-1">
                        Notes modifiées ({r.notes_modifiees.length})
                      </div>
                      <table className="w-full text-[10px]">
                        <thead className="bg-gray-100 dark:bg-gray-700/50">
                          <tr>
                            <th className="px-1.5 py-0.5 text-left">Matière</th>
                            <th className="px-1.5 py-0.5 text-center">Source</th>
                            <th className="px-1.5 py-0.5 text-right">Avant</th>
                            <th className="px-1.5 py-0.5 text-right">Après</th>
                            <th className="px-1.5 py-0.5 text-right">Gain</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.notes_modifiees.map((n: any, i: number) => (
                            <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                              <td className="px-1.5 py-0.5">
                                <span className="font-mono text-gray-500">{n.matiere_code}</span>
                                <span className="text-gray-700 dark:text-gray-300 ml-1">{n.matiere_nom}</span>
                              </td>
                              <td className="px-1.5 py-0.5 text-center">
                                <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${
                                  n.source === 'SR'
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                                    : n.source === 'SO'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                }`}>{n.source}</span>
                              </td>
                              <td className="px-1.5 py-0.5 text-right tabular-nums">{n.note_avant?.toFixed(2) ?? '—'}</td>
                              <td className="px-1.5 py-0.5 text-right tabular-nums font-bold">{n.note_apres?.toFixed(2) ?? '—'}</td>
                              <td className="px-1.5 py-0.5 text-right tabular-nums text-green-600 font-bold">
                                +{n.gain.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Stats avancées ──────────────────────────────────────────────────
const StatsView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    {/* Tranches moyennes avant */}
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
        <BarChart3 size={14} /> Distribution par tranche de moyenne AVANT rachat
      </h3>
      {data.par_tranche_avant.length === 0 ? (
        <div className="text-center text-xs text-gray-500 py-4">Aucune donnée</div>
      ) : (
        <div className="space-y-1.5">
          {data.par_tranche_avant.map((t: any) => {
            const max = Math.max(...data.par_tranche_avant.map((x: any) => x.nb_etu));
            const pct = max > 0 ? (t.nb_etu / max) * 100 : 0;
            return (
              <div key={t.libelle} className="flex items-center gap-3 text-xs">
                <div className="w-24 font-mono text-gray-600 dark:text-gray-400">{t.libelle}</div>
                <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden relative">
                  <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                  <div className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-white">
                    {t.nb_etu} étu · {t.nb_admis_grace_rachat} sauvés
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

const Kpi: React.FC<{
  label: string; value: any; couleur: string;
  icon?: React.ReactNode; hint?: string;
}> = ({ label, value, couleur, icon, hint }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
    {icon && <div className={`flex justify-center mb-1 ${couleur}`}>{icon}</div>}
    <div className={`text-xl font-bold tabular-nums ${couleur}`}>{value}</div>
    <div className="text-[10px] uppercase text-gray-500 font-semibold mt-0.5">{label}</div>
    {hint && <div className="text-[9px] text-gray-400 mt-0.5">{hint}</div>}
  </div>
);

export default RapportRachatsModal;
